import { useEffect, useState } from "react";
import { Globe, Languages } from "lucide-react";

/**
 * Top strip with (1) spoken languages by the business (informative)
 * and (2) Google Translate quick-switch buttons for the site visitor.
 *
 * We drive Google Translate via the `googtrans` cookie (the canonical way
 * for programmatic language switching). The Google widget picks it up on
 * page load and translates the whole page.
 */
const SPOKEN = ["Português", "English", "Español", "Français"];
const LANGS = [
    { code: "pt", label: "PT" },
    { code: "en", label: "EN" },
    { code: "es", label: "ES" },
    { code: "fr", label: "FR" },
];

function clearGoogTransCookies() {
    const hostname = window.location.hostname;
    const paths = ["/", ""];
    const domains = ["", hostname, "." + hostname];
    // Also try the base (parent) domain — e.g. .ohomemdaobra.pt for www.ohomemdaobra.pt
    const parts = hostname.split(".");
    if (parts.length >= 2) domains.push("." + parts.slice(-2).join("."));
    const past = "expires=Thu, 01 Jan 1970 00:00:00 GMT";
    paths.forEach(p => {
        domains.forEach(d => {
            const dPart = d ? `; domain=${d}` : "";
            const pPart = p ? `; path=${p}` : "";
            document.cookie = `googtrans=${pPart}${dPart}; ${past}`;
        });
    });
}

function setLanguage(langCode) {
    clearGoogTransCookies();
    if (langCode !== "pt") {
        const hostname = window.location.hostname;
        const parts = hostname.split(".");
        const value = `/pt/${langCode}`;
        // Set on multiple domain scopes to cover preview + custom domain + subdomain cases
        document.cookie = `googtrans=${value}; path=/`;
        document.cookie = `googtrans=${value}; path=/; domain=${hostname}`;
        if (parts.length >= 2) {
            document.cookie = `googtrans=${value}; path=/; domain=.${parts.slice(-2).join(".")}`;
        }
    }
    // Reload so Google Translate picks up the new cookie and translates the whole page.
    window.location.reload();
}

export default function TopBar() {
    const [active, setActive] = useState("pt");
    useEffect(() => {
        const m = document.cookie.match(/googtrans=\/[^/]+\/([a-z-]+)/);
        if (m) setActive(m[1]);
    }, []);
    return (
        <div className="notranslate bg-[hsl(var(--forest-deep))] text-white/85 text-xs" translate="no">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 py-2 flex flex-wrap items-center justify-between gap-y-1 gap-x-4">
                <div className="hidden sm:flex items-center gap-2 min-w-0">
                    <Globe className="w-3.5 h-3.5 text-[hsl(var(--copper))] flex-shrink-0" />
                    <span className="font-mono uppercase tracking-widest text-[10px] text-white/60">Falamos</span>
                    <span className="truncate">{SPOKEN.join(" · ")}</span>
                </div>
                <div className="flex items-center gap-2 ml-auto">
                    <Languages className="w-3.5 h-3.5 text-[hsl(var(--copper))]" />
                    <span className="font-mono uppercase tracking-widest text-[10px] text-white/60 hidden sm:inline">Traduzir</span>
                    <div className="flex items-center gap-0.5" data-testid="topbar-lang-switcher">
                        {LANGS.map((l) => (
                            <button
                                key={l.code}
                                type="button"
                                onClick={() => setLanguage(l.code)}
                                data-testid={`topbar-lang-${l.code}`}
                                aria-label={`Traduzir para ${l.label}`}
                                className={`px-2 py-0.5 rounded font-mono text-[11px] tracking-wider transition ${active === l.code ? "bg-[hsl(var(--copper))] text-white" : "text-white/70 hover:text-white hover:bg-white/10"}`}
                            >
                                {l.label}
                            </button>
                        ))}
                    </div>
                </div>
            </div>
            <div id="google_translate_element" className="hidden" aria-hidden="true" />
        </div>
    );
}
